"use client";

import Link from "next/link";
import {
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Activity,
  ArrowLeft,
  Music2,
  Pause,
  Pencil,
  Play,
  Plus,
  RefreshCw,

  Trash2,
} from "@/components/ui/icons";
import TransportBar from "@/features/session/components/TransportBar";
import { formatDuration } from "@/features/session/lib/format";
import { useLocalMp3Player } from "@/hooks/useLocalMp3Player";
import { useLocalMp3TestSession } from "@/hooks/useLocalMp3TestSession";
import { cn } from "@/lib/utils";
import { useStore } from "@/store";


function formatFileSize(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 MB";
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 1 : 2)} MB`;
}

export default function LocalMp3Player() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hiddenManualEditors, setHiddenManualEditors] = useState<Set<string>>(
    () => new Set(),
  );
  const baseline = useStore.use.baselineDraft();
  const preSurvey = useStore.use.preSurveyDraft();
  const {
    tracks,
    currentTrack,
    currentIndex,
    isPlaying,
    positionMs,
    durationMs,
    volume,
    error,
    recognitionError,
    addFiles,
    playTrackAt,
    togglePlay,
    nextTrack,
    previousTrack,
    seek,
    setVolume,
    removeTrack,
    clearTracks,
    retryRecognition,
    updateManualMetadata,
  } = useLocalMp3Player();
  const { finishSession, storageError } = useLocalMp3TestSession({
    baseline,
    preSurvey,
    tracks,
    currentTrack,
    positionMs,
    isPlaying,
  });


  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelection = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files) {
      addFiles(event.currentTarget.files);
    }
    event.currentTarget.value = "";
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files.length > 0) {
      addFiles(event.dataTransfer.files);
    }
  };

  return (
    <div
      className="session-depth-scope flex min-h-0 flex-1 flex-col"
      data-local-mp3-player=""
      data-session-depth-style="lift"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".mp3,audio/mpeg,audio/mp3"
        multiple
        className="sr-only"
        onChange={handleFileSelection}
      />

      <div className={cn("mx-auto flex w-full max-w-[72rem] flex-1 flex-col px-3 pt-3 sm:px-5 sm:pt-5", tracks.length > 0 ? "pb-[var(--session-transport-clearance,7rem)]" : "pb-5")}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <Button asChild variant="outline" size="icon-sm" aria-label="Back to sign in">
              <Link href="/login">
                <ArrowLeft className="size-4" />
              </Link>
            </Button>
            <div className="min-w-0">
              <p className="text-[0.6875rem] font-bold uppercase tracking-[0.18em] text-[color:var(--session-accent-ink)]">
                Local MP3
              </p>
              <h1 className="truncate text-xl font-semibold text-foreground sm:text-2xl">
                MP3 player
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link
                href="/local-player/session-data"
                onClick={finishSession}
              >
                <Activity className="size-4" />
                Test data
              </Link>
            </Button>
            {tracks.length > 0 ? (
              <Button variant="ghost" size="sm" onClick={clearTracks}>
                <Trash2 className="size-4" />
                Clear
              </Button>
            ) : null}
            <Button size="sm" onClick={openFilePicker}>
              <Plus className="size-4" />
              Add MP3s
            </Button>
          </div>
        </div>

        <div
          onDragEnter={(event) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
              setIsDragging(false);
            }
          }}
          onDrop={handleDrop}
          className={cn(
            "theme-surface-panel flex min-h-0 flex-1 flex-col overflow-hidden rounded-[1.25rem] border transition-[border-color,background,box-shadow] duration-200",
            isDragging &&
              "border-[color:var(--session-accent-border)] bg-[color:var(--session-accent-soft)] shadow-[0_0_0_4px_var(--session-accent-ring)]",
          )}
        >
          {tracks.length === 0 ? (
            <div className="grid min-h-[24rem] flex-1 place-items-center p-6 text-center">
              <div className="max-w-md">
                <span className="theme-button-secondary mx-auto flex size-16 items-center justify-center rounded-2xl border">
                  <Music2 className="size-8 text-[color:var(--session-accent-ink)]" />
                </span>
                <h2 className="mt-5 text-lg font-semibold text-foreground">
                  Choose music from this device
                </h2>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  Drag MP3 files here or select them from your computer. When a
                  track plays, it is sent to the IMAGS server for AcoustID
                  recognition and is not retained.
                </p>
                <Button className="mt-5" onClick={openFilePicker}>
                  <Plus className="size-4" />
                  Choose MP3 files
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="border-b border-border/70 px-4 py-3 sm:px-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="font-semibold text-foreground">Local queue</h2>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {tracks.length} {tracks.length === 1 ? "track" : "tracks"}{" "}
                      · AcoustID recognition enabled
                    </p>
                  </div>
                  <Music2 className="size-5 text-[color:var(--session-accent-ink)]" />
                </div>
              </div>

              <div className="scrollbar-theme min-h-0 flex-1 overflow-y-auto p-2 sm:p-3">
                <ol className="grid gap-1.5">
                  {tracks.map((track, index) => {
                    const isCurrent = index === currentIndex;
                    const isCurrentPlaying = isCurrent && isPlaying;
                    const identifiedTitle =
                      track.recognizedTitle || track.manualTitle.trim();
                    const identifiedArtist = track.recognizedTitle
                      ? track.artist
                      : track.manualArtist.trim();
                    const manualDetailsComplete = Boolean(
                      track.manualTitle.trim() && track.manualArtist.trim(),
                    );
                    const isManualEditorHidden = hiddenManualEditors.has(track.id);

                    return (
                      <li
                        key={track.id}
                        className={cn(
                          "group grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-xl border border-transparent px-2 py-2 transition-colors",
                          isCurrent
                            ? "border-[color:var(--session-accent-border)] bg-[color:var(--session-accent-soft)]"
                            : "hover:bg-[color:color-mix(in_srgb,var(--surface-card)_76%,transparent)]",
                        )}
                      >
                        <button
                          type="button"
                          data-local-track-copy=""
                          onClick={() => {
                            if (isCurrent) {
                              void togglePlay();
                            } else {
                              void playTrackAt(index);
                            }
                          }}
                          className={cn(
                            "theme-button-secondary inline-flex size-10 items-center justify-center rounded-lg border",
                            isCurrent && "theme-button-primary",
                          )}
                          aria-label={`${isCurrentPlaying ? "Pause" : "Play"} ${track.title}`}
                        >
                          {isCurrentPlaying ? (
                            <Pause className="size-4" weight="fill" />
                          ) : (
                            <Play className="size-4" weight="fill" />
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            if (isCurrent) {
                              void togglePlay();
                            } else {
                              void playTrackAt(index);
                            }
                          }}
                          className="min-w-0 text-left"
                        >
                          <span className="block truncate text-sm font-semibold text-foreground">
                            {track.title}
                            {identifiedTitle || identifiedArtist ? (
                              <span className="font-medium text-[color:var(--session-accent-ink)]">
                                {` · ${identifiedTitle || "Unknown song"}${identifiedArtist ? ` — ${identifiedArtist}` : ""}`}
                              </span>
                            ) : null}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                            {track.recognitionStatus === "recognizing"
                              ? "Recognizing… · "
                              : track.recognitionStatus === "not_found"
                                ? "No AcoustID match · "
                                : track.recognitionStatus === "failed"
                                  ? "Recognition failed · "
                                  : ""}
                            {formatFileSize(track.sizeBytes)}
                            {track.durationMs != null
                              ? ` · ${formatDuration(track.durationMs)}`
                              : ""}
                          </span>
                        </button>

                        <div className="flex items-center gap-1">
                          {track.recognitionStatus === "not_found" &&
                          manualDetailsComplete &&
                          isManualEditorHidden ? (
                            <button
                              type="button"
                              onClick={() =>
                                setHiddenManualEditors((current) => {
                                  const next = new Set(current);
                                  next.delete(track.id);
                                  return next;
                                })
                              }
                              className="theme-button-secondary inline-flex size-9 items-center justify-center rounded-lg border"
                              aria-label={`Edit manual details for ${track.title}`}
                              title="Edit manual song details"
                            >
                              <Pencil className="size-4" />
                            </button>
                          ) : null}
                          {track.recognitionStatus === "failed" ? (
                            <button
                              type="button"
                              onClick={() => retryRecognition(track.id)}
                              className="theme-button-secondary inline-flex size-9 items-center justify-center rounded-lg border"
                              aria-label={`Retry recognition for ${track.title}`}
                              title="Retry music recognition"
                            >
                              <RefreshCw className="size-4" />
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => removeTrack(track.id)}
                            className="inline-flex size-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-[color:var(--color-error-soft)] hover:text-[color:var(--color-error-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
                            aria-label={`Remove ${track.title}`}
                            title="Remove from local queue"
                          >
                            <Trash2 className="size-4" />
                          </button>
                        </div>

                        {track.recognitionStatus === "not_found" &&
                        !isManualEditorHidden ? (
                          <div className="col-span-3 grid gap-2 rounded-lg border border-border/70 bg-background/55 p-3 sm:grid-cols-2">
                            <p className="text-xs text-muted-foreground sm:col-span-2">
                              AcoustID could not identify this file. Add its details
                              manually so they are included in the session data.
                            </p>
                            <label className="grid gap-1 text-xs font-medium text-foreground">
                              Song name
                              <Input
                                value={track.manualTitle}
                                onChange={(event) =>
                                  updateManualMetadata(
                                    track.id,
                                    "manualTitle",
                                    event.currentTarget.value,
                                  )
                                }
                                maxLength={256}
                                placeholder="Enter song name"
                                aria-label={`Manual song name for ${track.title}`}
                              />
                            </label>
                            <label className="grid gap-1 text-xs font-medium text-foreground">
                              Artist
                              <Input
                                value={track.manualArtist}
                                onChange={(event) =>
                                  updateManualMetadata(
                                    track.id,
                                    "manualArtist",
                                    event.currentTarget.value,
                                  )
                                }
                                maxLength={256}
                                placeholder="Enter artist"
                                aria-label={`Manual artist for ${track.title}`}
                              />
                            </label>
                            <div className="flex justify-end sm:col-span-2">
                              <Button
                                type="button"
                                size="sm"
                                disabled={!manualDetailsComplete}
                                onClick={() =>
                                  setHiddenManualEditors((current) => {
                                    const next = new Set(current);
                                    next.add(track.id);
                                    return next;
                                  })
                                }
                              >
                                Save details
                              </Button>
                            </div>
                          </div>
                        ) : null}
                      </li>
                    );
                  })}
                </ol>
              </div>
            </>
          )}
        </div>

        {error ? (
          <p
            role="status"
            className="mt-3 rounded-xl border border-[color:var(--color-error-border)] bg-[color:var(--color-error-soft)] px-3 py-2 text-sm text-[color:var(--color-error-foreground)]"
          >
            {error}
          </p>
        ) : null}

        {recognitionError ? (
          <p
            role="status"
            className="mt-3 rounded-xl border border-[color:var(--color-error-border)] bg-[color:var(--color-error-soft)] px-3 py-2 text-sm text-[color:var(--color-error-foreground)]"
          >
            {recognitionError}
          </p>
        ) : null}

        {storageError ? (
          <p
            role="status"
            className="mt-3 rounded-xl border border-[color:var(--color-error-border)] bg-[color:var(--color-error-soft)] px-3 py-2 text-sm text-[color:var(--color-error-foreground)]"
          >
            {storageError}
          </p>
        ) : null}
      </div>

      {tracks.length > 0 ? (
        <TransportBar
          isPlaying={isPlaying}
          onPlayPause={() => {
            void togglePlay();
          }}
          onPrev={() => {
            void previousTrack();
          }}
          onNext={() => {
            void nextTrack();
          }}
          trackTitle={currentTrack?.title ?? null}
          trackArtist={
            currentTrack?.recognizedTitle
              ? `${currentTrack.recognizedTitle}${currentTrack.artist ? ` — ${currentTrack.artist}` : ""}`
              : currentTrack?.manualTitle.trim() || currentTrack?.manualArtist.trim()
                ? `${currentTrack.manualTitle.trim() || "Unknown song"}${currentTrack.manualArtist.trim() ? ` — ${currentTrack.manualArtist.trim()}` : ""}`
              : "Local MP3"
          }
          trackUri={currentTrack ? `local:${currentTrack.id}` : null}
          positionMs={positionMs}
          durationMs={durationMs}
          onSeek={seek}
          volume={volume}
          onVolumeChange={setVolume}
          playbackDisabled={!currentTrack}
        />
      ) : null}
    </div>
  );
}
